import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    IconButton,
    DialogContent,
    Button,
    Typography,
    TextField,
    Select,
    MenuItem,
    SelectChangeEvent,
    Autocomplete,
    useTheme
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'sonner';
import { BenefitsType } from 'Application/models';
import { useBenefitsStore } from 'Application/store';
import { useParams } from 'react-router-dom';
import { ThemeMode } from 'config';

interface SetBenefit {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}

const SetBenefit: React.FC<SetBenefit> = ({ isOpen, setIsOpen }) => {
    const [benefitType, setBenefitType] = useState<string | BenefitsType>('');

    const { setBenefitsData, setBenefit, benefitsData, getBenefits, isLoading } = useBenefitsStore(); // Access the store
    const { planID } = useParams<{ planID: string }>(); // Access planID from route parameters

    const [formData, setFormData] = useState({
        benefitName: '',
        value: 0,
        type: '',
        numberOfHours: 0,
        hoursCost: 0,
        costPerDay: 0,
        percentageOnPremium: 0,
        coverExcess: 0
    });

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    useEffect(() => {
        getBenefits();
    }, []);

    const handleChange = (e: SelectChangeEvent<BenefitsType>) => {
        setBenefitType(e.target.value as BenefitsType);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const setBenefitsData = {
            ...formData,
            type: benefitType
        };

        try {
            await setBenefit(setBenefitsData, planID);
            toast.success('Benefit values added successfully');
            setIsOpen(false);
            // Clear the form data
            setFormData({
                benefitName: '',
                value: 0,
                type: '',
                numberOfHours: 0,
                hoursCost: 0,
                costPerDay: 0,
                percentageOnPremium: 0,
                coverExcess: 0
            });
            setBenefitType(''); // Reset the benefit type
        } catch (error) {
            toast.error(`Failed to set benefit: ${error}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Set Plan Benefits </span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Benefit Name
                        </Typography>
                        <Autocomplete
                            id="benefitName"
                            options={benefitsData}
                            getOptionLabel={(option) => option.name}
                            onChange={(event, value) => setFormData({ ...formData, benefitName: value?.name || '' })}
                            renderInput={(params) => <TextField {...params} required name="benefitName" fullWidth />}
                        />
                    </div>

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Type of Benefit?
                        </Typography>
                        <Select value={benefitType} onChange={handleChange} fullWidth displayEmpty>
                            <MenuItem value="" disabled>
                                Select type of benefit
                            </MenuItem>
                            <MenuItem value={BenefitsType.DAILY}>Daily</MenuItem>
                            <MenuItem value={BenefitsType.FIXED}>Fixed</MenuItem>
                            <MenuItem value={BenefitsType.HOURLY}>Hourly</MenuItem>
                            <MenuItem value={BenefitsType.PERCENTAGE}>Percentage (%)</MenuItem>
                        </Select>
                    </div>

                    {/* Conditionally render fields based on benefitType */}
                    {benefitType === BenefitsType.DAILY && (
                        <>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Cost Per Day
                                </Typography>
                                <TextField
                                    placeholder="Enter cost per day"
                                    required
                                    name="costPerDay"
                                    variant="outlined"
                                    value={formData.costPerDay}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>

                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Maximum Accumalition Value
                                </Typography>
                                <TextField
                                    placeholder="Enter maximum accumulator value"
                                    required
                                    name="value"
                                    variant="outlined"
                                    value={formData.value}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}

                    {benefitType === BenefitsType.HOURLY && (
                        <>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Total Number of Hours
                                </Typography>
                                <TextField
                                    placeholder="Enter number of hours"
                                    required
                                    name="numberOfHours"
                                    variant="outlined"
                                    value={formData.numberOfHours}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Cost of the total Number of Hours
                                </Typography>
                                <TextField
                                    placeholder="Enter cost of the number of Hours"
                                    required
                                    name="hoursCost"
                                    variant="outlined"
                                    value={formData.hoursCost}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>

                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Maximum Cost Allowed
                                </Typography>
                                <TextField
                                    placeholder="Enter maximum cost"
                                    required
                                    name="value"
                                    variant="outlined"
                                    value={formData.value}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}

                    {benefitType === BenefitsType.FIXED && (
                        <>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Value
                                </Typography>
                                <TextField
                                    placeholder="Enter maximum cost"
                                    required
                                    name="value"
                                    variant="outlined"
                                    value={formData.value}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}

                    {benefitType === BenefitsType.PERCENTAGE && (
                        <>
                            <div style={{ paddingBottom: '1rem' }}>
                                <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                                    Percentage on Premium
                                </Typography>
                                <TextField
                                    placeholder="Enter percentage on premium"
                                    required
                                    name="percentageOnPremium"
                                    variant="outlined"
                                    value={formData.percentageOnPremium}
                                    onChange={handleInputChange}
                                    type="number"
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                />
                            </div>
                        </>
                    )}

                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Cover Excess (USD)
                        </Typography>
                        <TextField
                            placeholder="Cover Excess"
                            required
                            name="coverExcess"
                            variant="outlined"
                            value={formData.coverExcess}
                            onChange={handleInputChange}
                            type="number"
                            inputProps={{ min: 0 }}
                            fullWidth
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default SetBenefit;
